import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getOrder, getSettings } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Sprout, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const InvoicePage = () => {
  const { id } = useParams();
  const { t, lang } = useApp();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getOrder(id), getSettings()])
      .then(([o, s]) => { setOrder(o); setSettings(s); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#2D5A27]" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-lg mx-auto p-8 text-center">
        <p className="text-slate-600">Order not found.</p>
        <Link to="/" className="text-[#2D5A27] font-semibold mt-4 inline-block">← Home</Link>
      </div>
    );
  }

  const shopName = settings?.shop_name || "Krishidut Trading";
  const shopAddress = settings?.shop_address || "";
  const shopPhone = settings?.shop_phone || settings?.shop_whatsapp || "";
  const date = new Date(order.created_at);

  return (
    <div className="min-h-screen bg-[#F9F7F3] py-6 px-4 print:p-0 print:bg-white" data-testid="invoice-page">
      {/* Action bar - hidden on print */}
      <div className="max-w-3xl mx-auto flex items-center justify-between mb-4 print:hidden">
        <Link to="/orders" className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#2D5A27]" data-testid="back-to-orders">
          <ArrowLeft className="w-4 h-4" /> {t("myOrders")}
        </Link>
        <Button
          onClick={() => window.print()}
          data-testid="print-invoice-btn"
          className="rounded-full bg-[#2D5A27] hover:bg-[#21421D] text-white h-10 px-5"
        >
          <Printer className="w-4 h-4 mr-1.5" /> {t("print")} / PDF
        </Button>
      </div>

      {/* Invoice sheet */}
      <div className="max-w-3xl mx-auto bg-white border border-[#E2E8F0] rounded-lg print:border-0 print:rounded-none p-6 md:p-10 shadow-sm print:shadow-none" data-testid="invoice-sheet">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 pb-6 border-b-2 border-[#2D5A27]">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#2D5A27] flex items-center justify-center shrink-0">
              <Sprout className="w-7 h-7 text-white" strokeWidth={2.2} />
            </div>
            <div>
              <h1 className={`font-heading font-bold text-2xl text-[#1E293B] ${lang === "hi" ? "font-devanagari" : ""}`}>{shopName}</h1>
              {settings?.owner_name && <p className="text-xs text-slate-600 mt-0.5">{t("proprietor")}: {settings.owner_name}</p>}
              {shopAddress && <p className="text-sm text-slate-600 mt-0.5">{shopAddress}</p>}
              {shopPhone && <p className="text-sm text-slate-600">{lang === "hi" ? "फोन" : "Phone"}: +{shopPhone}</p>}
              {settings?.gst_number && <p className="text-xs text-slate-600 font-mono mt-0.5">GSTIN: {settings.gst_number}</p>}
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-[#D97706] font-bold">{t("invoiceTitle")}</p>
            <p className="font-mono font-semibold text-[#1E293B] mt-1" data-testid="invoice-order-number">{order.order_number}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {date.toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { dateStyle: "long" })}
            </p>
          </div>
        </div>

        {/* Bill to + payment */}
        <div className="grid grid-cols-2 gap-6 mt-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">{t("billTo")}</p>
            <p className={`font-heading font-semibold text-[#1E293B] ${lang === "hi" ? "font-devanagari" : ""}`}>{order.delivery_name}</p>
            <p className="text-sm text-slate-600 mt-0.5">{order.delivery_mobile}</p>
            <p className="text-sm text-slate-600 mt-0.5 whitespace-pre-line">{order.delivery_address}</p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">{t("paymentMethod")}</p>
            <p className="font-heading font-semibold text-[#1E293B]">
              {order.payment_method === "UPI" ? "UPI (Pre-paid)" : "Cash on Delivery"}
            </p>
            {order.payment_ref && (
              <p className="text-xs text-slate-500 font-mono mt-0.5">Ref: {order.payment_ref}</p>
            )}
            <div className="mt-3">
              <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">{t("orderStatus")}</p>
              <p className="font-semibold text-[#2D5A27]">{t(order.status)}</p>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="mt-8">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E2E8F0] text-left">
                <th className="py-2 px-2 font-heading font-semibold text-[#1E293B]">{t("itemName")}</th>
                <th className="py-2 px-2 font-heading font-semibold text-[#1E293B] text-center w-16">Qty</th>
                <th className="py-2 px-2 font-heading font-semibold text-[#1E293B] text-right w-28">{t("unitPrice")}</th>
                <th className="py-2 px-2 font-heading font-semibold text-[#1E293B] text-right w-28">{t("amount")}</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((it, i) => {
                const name = lang === "hi" ? it.name_hi : it.name_en;
                return (
                  <tr key={i} className="border-b border-[#E2E8F0]">
                    <td className={`py-3 px-2 ${lang === "hi" ? "font-devanagari" : ""}`}>
                      <p className="font-medium text-[#1E293B]">{name}</p>
                      <p className="text-xs text-slate-500">{it.unit}</p>
                    </td>
                    <td className="py-3 px-2 text-center">{it.quantity}</td>
                    <td className="py-3 px-2 text-right">₹{it.price.toFixed(2)}</td>
                    <td className="py-3 px-2 text-right font-semibold">₹{(it.price * it.quantity).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="mt-6 flex justify-end">
          <div className="w-full sm:w-72 space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">{t("subtotal")}</span>
              <span className="font-semibold">₹{order.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">{t("deliveryFee")}</span>
              <span className="font-semibold">₹{order.delivery_fee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg pt-2 border-t-2 border-[#2D5A27]">
              <span className="font-heading font-bold text-[#1E293B]">{t("total")}</span>
              <span className="font-heading font-bold text-[#2D5A27]">₹{order.total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {order.notes && (
          <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">{lang === "hi" ? "नोट" : "Notes"}</p>
            <p className="text-sm text-slate-700">{order.notes}</p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-[#E2E8F0] text-center">
          <p className={`font-heading font-semibold text-[#2D5A27] ${lang === "hi" ? "font-devanagari" : ""}`}>{t("thankYou")}</p>
          <p className="text-xs text-slate-500 mt-1">{t("invoiceFooter")}</p>
          {(settings?.fertilizer_license || settings?.seed_license || settings?.pesticide_license) && (
            <div className="mt-4 pt-4 border-t border-dashed border-[#E2E8F0] text-[10px] text-slate-500 font-mono space-y-0.5">
              {settings?.fertilizer_license && <p>Fertilizer License: {settings.fertilizer_license}</p>}
              {settings?.seed_license && <p>Seed License: {settings.seed_license}</p>}
              {settings?.pesticide_license && <p>Pesticide License: {settings.pesticide_license}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoicePage;
